import { useState } from 'react';
import { useI18n } from '../i18n';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Country flag component using flagcdn.com for reliable cross-platform display
export const CountryFlag = ({ countryCode, size = 'md', className }) => {
  const [hasError, setHasError] = useState(false);
  
  const sizeMap = {
    sm: 'w-4 h-3',
    md: 'w-5 h-4',
    lg: 'w-6 h-5',
    xl: 'w-8 h-6'
  };

  // Fallback to text if image fails to load
  if (hasError) {
    return (
      <span className={cn(sizeMap[size], 'flex items-center justify-center bg-muted rounded-sm text-[8px] font-bold', className)}>
        {countryCode}
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
      alt={countryCode}
      className={cn(sizeMap[size], 'rounded-sm object-cover', className)}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
};

// Compact language switcher for header/navigation
export const LanguageSwitcher = ({ variant = 'icon' }) => {
  const { language, changeLanguage, getCurrentLanguage, supportedLanguages } = useI18n();
  const [open, setOpen] = useState(false);
  const currentLang = getCurrentLanguage();

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    const lang = supportedLanguages.find(l => l.code === langCode);
    toast.success(`Language changed to ${lang?.name || langCode}`);
    setOpen(false);
  };

  if (variant === 'icon') {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="language-switcher-btn"
          >
            <Globe className="h-5 w-5" />
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-3 rounded-sm overflow-hidden">
              <CountryFlag countryCode={currentLang?.flag || 'US'} size="sm" />
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Select Language
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                "flex items-center gap-3 cursor-pointer",
                language === lang.code && "bg-emerald-500/10"
              )}
              data-testid={`lang-option-${lang.code}`}
            >
              <CountryFlag countryCode={lang.flag} size="md" />
              <div className="flex-1">
                <p className="font-medium text-sm">{lang.nativeName}</p>
                <p className="text-xs text-muted-foreground">{lang.name}</p>
              </div>
              {language === lang.code && (
                <Check className="w-4 h-4 text-emerald-500" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Button variant with label
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2"
          data-testid="language-switcher-btn"
        >
          <CountryFlag countryCode={currentLang?.flag || 'US'} size="md" />
          <span className="hidden sm:inline">{currentLang?.nativeName}</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Select Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              "flex items-center gap-3 cursor-pointer",
              language === lang.code && "bg-emerald-500/10"
            )}
            data-testid={`lang-option-${lang.code}`}
          >
            <CountryFlag countryCode={lang.flag} size="md" />
            <div className="flex-1">
              <p className="font-medium text-sm">{lang.nativeName}</p>
              <p className="text-xs text-muted-foreground">{lang.name}</p>
            </div>
            {language === lang.code && (
              <Check className="w-4 h-4 text-emerald-500" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
